import { Controller } from "@hotwired/stimulus"

// Mirrors dripshipper-react src/util/validation.js + regex.js
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/
const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
const NAME_REGEX = /^[a-zA-z]+([\s][a-zA-Z]+)*$/

const MESSAGES = {
  full_name: { required: "Full Name is required", invalid: "Full Name is invalid" },
  email: { required: "Email is required", invalid: "Email is invalid" },
  password: {
    required: "Password is required",
    invalid:
      "Password is invalid. It is require at least 8 characters, mixture of both uppercase and lowercase letters, inclusion of at least one special character & 1 or more number."
  },
  password_confirmation: {
    required: "Confirm Password is required",
    invalid: "Password is not matched"
  }
}

export default class extends Controller {
  static targets = ["input", "error"]

  connect() {
    this.inputTargets.forEach((input) => {
      const field = input.dataset.authFormField
      const errorEl = this.errorTargets.find((el) => el.dataset.authFormField === field)
      if (!errorEl?.textContent?.trim()) return

      input.setAttribute("aria-invalid", "true")
      if (errorEl.id) input.setAttribute("aria-describedby", errorEl.id)
      errorEl.hidden = false
    })
    this.syncFormValidity()
  }

  validate(event) {
    const input = event.currentTarget
    this.validateField(input)
    this.syncFormValidity()
  }

  validateAll(event) {
    let valid = true
    this.inputTargets.forEach((input) => {
      if (!this.validateField(input)) valid = false
    })
    this.syncFormValidity(valid)

    if (!valid) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }

  validateField(input) {
    const field = input.dataset.authFormField
    if (!field) return true

    const message = this.messageFor(field, input)
    this.setError(field, message)
    input.setAttribute("aria-invalid", message ? "true" : "false")
    return !message
  }

  messageFor(field, input) {
    const value = input.value
    const password = this.passwordValue()
    const confirm = this.confirmPasswordValue()

    switch (field) {
      case "full_name":
        if (!value.trim()) return MESSAGES.full_name.required
        if (!NAME_REGEX.test(value)) return MESSAGES.full_name.invalid
        return ""
      case "email":
        if (!value.trim()) return MESSAGES.email.required
        if (!EMAIL_REGEX.test(value.toLowerCase())) return MESSAGES.email.invalid
        return ""
      case "password":
        if (!value) return MESSAGES.password.required
        if (!PASSWORD_REGEX.test(value)) return MESSAGES.password.invalid
        if (confirm && confirm !== value) {
          this.setError("password_confirmation", MESSAGES.password_confirmation.invalid)
        } else if (confirm && confirm === value) {
          this.setError("password_confirmation", "")
        }
        return ""
      case "password_confirmation":
        if (!value) return MESSAGES.password_confirmation.required
        if (value !== password || !password) return MESSAGES.password_confirmation.invalid
        return ""
      default:
        return ""
    }
  }

  passwordValue() {
    return this.inputTargets.find((el) => el.dataset.authFormField === "password")?.value || ""
  }

  confirmPasswordValue() {
    return (
      this.inputTargets.find((el) => el.dataset.authFormField === "password_confirmation")?.value ||
      ""
    )
  }

  setError(field, message) {
    const errorEl = this.errorTargets.find((el) => el.dataset.authFormField === field)
    if (!errorEl) return

    errorEl.textContent = message
    errorEl.hidden = !message
    const input = this.inputTargets.find((el) => el.dataset.authFormField === field)
    if (input) {
      const errorId = errorEl.id
      if (message && errorId) {
        input.setAttribute("aria-describedby", errorId)
      } else {
        input.removeAttribute("aria-describedby")
      }
    }
  }

  syncFormValidity(valid = null) {
    if (valid === null) {
      valid = this.inputTargets.every((input) => !this.messageFor(input.dataset.authFormField, input))
    }
    this.element.dataset.authFormValid = valid ? "true" : "false"
  }
}
